import * as React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { media } from 'styles/utils';

import Carousel, { useCarousel } from 'components/Carousel';
import ClientOnly from 'components/ClientOnly';
import Container from 'components/Container';
import Icon from 'components/Icon';
import Row from 'components/Row';
import Stack from 'components/Stack';
import Text from 'components/Text';

const Section = styled.section`
  overflow: hidden;
  padding: 4.25rem 1rem 4.25rem;
  position: relative;
`;

const Card = styled(Stack)`
  background-image: linear-gradient(
    rgb(29 41 57 / 60%) 35%,
    rgb(16 24 40 / 60%)
  );
  border-radius: 1.5rem;
  border: 0.125rem solid #344054;
  padding: clamp(1.5rem, 3vw, 2.5rem) clamp(1.5rem, 3vw, 2.5rem)
    clamp(2rem, 5vw, 3.75rem);
  line-height: 1.5;
  text-wrap: balance;

  p {
    -webkit-font-smoothing: antialiased;
  }
`;

const OverflowWrapper = styled.div`
  margin-left: -1rem;
  margin-right: -1rem;
  padding: 0 1rem;
  overflow: hidden;
`;

const CarouselWrapper = styled.div`
  ${media.max('lg')} {
    display: none;
  }
`;

const StyledStack = styled(Stack)`
  ${media.min('lg')} {
    display: none;
  }
`;

const StyledTabs = styled(Carousel.Tabs)`
  align-items: stretch;
  flex-wrap: nowrap;
  gap: 1.25rem;

  & > * {
    flex: 1 0 auto;
  }
`;

const StyledTab = styled(CarouselTab)`
  align-items: start;
  display: flex;
  flex-flow: column nowrap;
  gap: 0.625rem;
  text-align: left;

  ${media.min('lg')} {
    border-radius: 0.75rem;
    overflow: hidden;
    padding: 1.75rem;
    position: relative;
  }

  [aria-selected='false']:not(:hover) & {
    opacity: 0.6;
  }

  strong {
    font-size: 1rem;
    font-weight: bold;
  }

  small {
    -webkit-font-smoothing: antialiased;
    font-size: 1rem;
  }
`;

const StyledSlide = styled(CarouselSlide)`
  display: flex;
  flex-flow: column nowrap;
  gap: 3rem;
  padding: 3rem 0;

  & > :first-child {
    filter: drop-shadow(0px 2px 4px #0000000d)
      drop-shadow(0px 2px 10px #0000000d);
  }
`;

const carouselItems = [
  {
    tab: {
      icon: 'truck',
      title: 'CTO Review',
      subtitle: 'Cost allocations',
    },
    slide: (
      <StaticImage
        alt=""
        placeholder="none"
        src="../../images/editorial-graphics/feature-illustration-tab-cto-review.png"
        width={1024}
      />
    ),
  },
  {
    tab: {
      icon: 'rocket',
      title: 'State of Eng',
      subtitle: 'Predictable delivery',
    },
    slide: (
      <StaticImage
        alt=""
        placeholder="none"
        src="../../images/editorial-graphics/feature-illustration-tab-state-of-eng.png"
        width={1024}
      />
    ),
  },
  {
    tab: {
      icon: 'warning-sign',
      title: 'Weekly Dev Kickoff',
      subtitle: 'Planning and ops',
    },
    slide: (
      <StaticImage
        alt=""
        placeholder="none"
        src="../../images/editorial-graphics/feature-illustration-tab-dev-kickoff.png"
        width={1024}
      />
    ),
  },
  {
    tab: {
      icon: 'wrench',
      title: 'DevEx Surveys',
      subtitle: 'Developer happiness',
    },
    slide: (
      <StaticImage
        alt=""
        placeholder="none"
        src="../../images/editorial-graphics/feature-illustration-tab-devex-review.png"
        width={1024}
      />
    ),
  },
];

export default function DescriptionFeatureSection() {
  const carousel = useCarousel({
    autoplay: { pauseOnHover: false },
    mode: 'fade',
  });

  return (
    <Section>
      <Container as={Stack} gap="2.625rem" size="md">
        <Text as="h2" variant="heading.section">
          Reviews for every level of Engineering
        </Text>
        <Text variant="md">
          No matter the rituals or cadences your engineering organization
          follows, Sleuth provides the metrics to support them. Review templates
          deliver data-rich, auto-generated insights that write themselves. We
          bring your metrics to life for high-value discussions.
        </Text>
        <OverflowWrapper>
          <Container size="md">
            <ClientOnly>
              <StyledStack as="ul" gap="2rem" role="list">
                {carouselItems.map((item) => (
                  <li key={item.tab.title}>
                    <StyledTab {...item} />
                    <StyledSlide {...item} />
                  </li>
                ))}
              </StyledStack>
              <CarouselWrapper>
                <Carousel carousel={carousel}>
                  <StyledTabs>
                    {carouselItems.map((item, i) => (
                      <StyledTab key={item.tab.title} {...item} />
                    ))}
                  </StyledTabs>
                  <Carousel.Slides>
                    {carouselItems.map((item, index) => (
                      <Carousel.Slide key={index}>
                        <StyledSlide {...item} />
                      </Carousel.Slide>
                    ))}
                  </Carousel.Slides>
                </Carousel>
              </CarouselWrapper>
            </ClientOnly>
          </Container>
        </OverflowWrapper>
      </Container>
    </Section>
  );
}

function CarouselTab({ className, current, tab }) {
  const { icon, subtitle, title } = tab;
  return (
    <span className={className}>
      <strong>
        <Icon color="#afd6ec" name={icon} size="1.5rem" />
        &nbsp;
        {title}
      </strong>
      <small>{subtitle}</small>
    </span>
  );
}

function CarouselSlide({ className, slide, title }) {
  return (
    <figure className={className} key={`slide-${title}`}>
      {slide}
    </figure>
  );
}
